import { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

const SELECT = "SELECT";
const PDF_ENDPOINT = "/FillMyForm/BOM_AOF1/JointPrint.php";
const STORAGE_KEY = "smart-solutions-bom-joint";

const options = {
  gender: [[SELECT, SELECT], ["MALE", "MALE"], ["FEMALE", "FEMALE"]],
  marital: [[SELECT, SELECT], ["MARRIED", "MARRIED"], ["UNMARRIED", "UNMARRIED"]],
  guardianRelation: [[SELECT, SELECT], ["FATHER", "FATHER"], ["MOTHER", "MOTHER"], ["BROTHER", "BROTHER"], ["SISTER", "SISTER"], ["OTHER", "OTHER"]],
  religion: [[SELECT, SELECT], ["HINDU", "HINDU"], ["MUSLIM", "MUSLIM"], ["CHRISTIAN", "CHRISTIAN"], ["SIKH", "SIKH"]],
  category: [[SELECT, SELECT], ["GENERAL", "GENERAL"], ["OBC", "OBC"], ["SC", "SC"], ["ST", "ST"]],
  qualification: [[SELECT, SELECT], ["ILLITERATE", "Illiterate"], ["BSSC", "Below SSC"], ["SSC", "SSC"], ["HSC", "HSC"], ["GRADUATE", "Graduate"], ["PGRADUATE", "Post Graduate"], ["PROFFESIONAL", "Professional"], ["OTHERS", "Others"]],
  occupation: [[SELECT, SELECT], ["SERVICE", "Service"], ["PRIVATE_SECTOR", "Private Sector"], ["PUBLIC_SECTOR", "Public Sector"], ["GOVERNMENT_SECTOR", "Government Sector"], ["OTHERS", "Others"], ["PROFESSIONAL", "Professional"], ["SELF_EMPLOYED", "Self Employed"], ["RETIRED", "Retired"], ["HOUSEWIFE", "Housewife"], ["STUDENT", "Student"], ["BUSINESS", "Business"], ["NOT_CATEGORISED", "Not categorised"]],
  yesNo: [["YES", "YES"], ["NO", "NO"]],
  nomineeRelation: [[SELECT, SELECT], ["FATHER", "FATHER"], ["MOTHER", "MOTHER"], ["HUSBAND", "HUSBAND"], ["WIFE", "WIFE"], ["SON", "SON"], ["DAUGHTER", "DAUGHTER"], ["BROTHER", "BROTHER"], ["SISTER", "SISTER"], ["OTHER", "OTHER"]],
};

const applicantFields = (prefix, includeBranch = false) => [
  ...(includeBranch ? [
    { name: "branch", label: "Branch Name", maxLength: 18 },
    { name: "code", label: "Branch Code", maxLength: 5 },
  ] : []),
  { name: `${prefix}cifno`, label: "CIF Number", maxLength: 11, numeric: true, optional: true },
  { name: `${prefix}acno`, label: "Account Number", maxLength: 11, numeric: true, optional: true },
  { name: `${prefix}name`, label: "Applicant Name", maxLength: 36, alphaOnly: true },
  { name: `${prefix}dob`, label: "Date of Birth", type: "date" },
  { name: `${prefix}gender`, label: "Gender", options: options.gender },
  { name: `${prefix}mstatus`, label: "Marital Status", options: options.marital },
  { name: `${prefix}aadhaarno`, label: "Aadhaar Number", maxLength: 12, numeric: true, aadhaar: true },
  { name: `${prefix}pan`, label: "PAN Number", maxLength: 10, pan: true, optional: true },
  { name: `${prefix}fname`, label: "Father's Name", maxLength: 36, alphaOnly: true },
  { name: `${prefix}mname`, label: "Mother's Name", maxLength: 33, alphaOnly: true },
  { name: `${prefix}gname`, label: "Guardian's Name", maxLength: 36, alphaOnly: true, guardian: true, prefix },
  { name: `${prefix}grelation`, label: "Guardian's Relation", options: options.guardianRelation, guardian: true, prefix },
];

const addressFields = (prefix) => [
  { name: `${prefix}add1`, label: "Address Line 1", maxLength: 35 },
  { name: `${prefix}city`, label: "City", maxLength: 13 },
  { name: `${prefix}dist`, label: "District", maxLength: 23 },
  { name: `${prefix}state`, label: "State", maxLength: 13 },
  { name: `${prefix}pin`, label: "PIN", maxLength: 6, numeric: true, pin: true },
  { name: `${prefix}bplace`, label: "Birth Place", maxLength: 13 },
  { name: `${prefix}mobile`, label: "Mobile", maxLength: 10, numeric: true, mobile: true },
  { name: `${prefix}income`, label: "Annual Income", maxLength: 8, numeric: true },
  { name: `${prefix}religion`, label: "Religion", options: options.religion },
  { name: `${prefix}category`, label: "Category", options: options.category },
  { name: `${prefix}qualification`, label: "Qualification", options: options.qualification },
  { name: `${prefix}occupation`, label: "Occupation", options: options.occupation },
];

const firstPersonalFields = applicantFields("fa", true);
const firstAddressFields = addressFields("fa");
const secondPersonalFields = applicantFields("sa");
const secondAddressFields = addressFields("sa");
const nominationFields = [
  { name: "isnomination", label: "Is Nomination?", options: options.yesNo },
  { name: "nname", label: "Nominee Name", maxLength: 36, alphaOnly: true, nomination: true },
  { name: "nrelation", label: "Relation", options: options.nomineeRelation, nomination: true },
  { name: "ndob", label: "Date of Birth", type: "date", nomination: true },
  { name: "ngname", label: "Guardian's Name", maxLength: 36, alphaOnly: true, nomination: true, nomineeGuardian: true },
  { name: "ngage", label: "Guardian's Age", maxLength: 2, numeric: true, nomination: true, nomineeGuardian: true },
];

const allFields = [
  ...firstPersonalFields,
  ...firstAddressFields,
  ...secondPersonalFields,
  ...secondAddressFields,
  ...nominationFields,
];

const initialFormData = Object.fromEntries(
  allFields.map((field) => [
    field.name,
    field.name === "isnomination" ? "YES" : field.options ? SELECT : "",
  ]),
);

function getAge(dateValue) {
  if (!dateValue) return null;
  const dob = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDifference = today.getMonth() - dob.getMonth();
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < dob.getDate())) age -= 1;
  return age;
}

function validateField(field, value, data) {
  const valueText = String(value ?? "").trim();
  if (field.spouse && data[`${field.prefix}mstatus`] !== "MARRIED") return "";
  if (field.guardian && (getAge(data[`${field.prefix}dob`]) ?? 18) >= 18) return "";
  if (field.nomination && data.isnomination === "NO") return "";
  if (field.nomineeGuardian && (getAge(data.ndob) ?? 18) >= 18) return "";
  if (!field.optional && (!valueText || valueText === SELECT)) return `${field.label} is required.`;
  if (!valueText || valueText === SELECT) return "";
  if (field.alphaOnly && !/^[A-Za-z ]+$/.test(valueText)) {
    return `${field.label} must contain alphabet letters only.`;
  }
  if (field.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(valueText)) {
    return "Enter a valid PAN number (e.g. ABCDE1234F).";
  }
  if (field.aadhaar && !/^\d{12}$/.test(valueText)) {
    return "Aadhaar number must contain exactly 12 digits.";
  }
  if (field.pin && !/^\d{6}$/.test(valueText)) {
    return "PIN must contain exactly 6 digits.";
  }
  if (field.mobile && !/^[6-9][0-9]{9}$/.test(valueText)) return "Enter a valid 10-digit mobile number.";
  return "";
}

export default function Joint() {
  const [currentTab, setCurrentTab] = useState(0);
  const [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [formData, setFormData] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      return saved ? { ...initialFormData, ...JSON.parse(saved) } : initialFormData;
    } catch {
      return initialFormData;
    }
  });
  const [errors, setErrors] = useState({});
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const nextButtonRef = useRef(null);


  useEffect(() => {
    const controller = new AbortController();

    const loadAutofill = async () => {
      try {
        const response = await fetch(
          `/backend/api/autofill.php?form=${encodeURIComponent("BOM_AOF-J1")}`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.message || "Unable to load autofill details.");
        }

        if (!result.found || !result.data) {
          return;
        }

        setFormData((previous) => ({
          ...previous,

          branch: previous.branch || result.data.branch || "",
          code: previous.code || result.data.code || "",

          facity: previous.facity || result.data.city || "",
          fadist: previous.fadist || result.data.dist || "",
          fastate: previous.fastate || result.data.state || "",
          fapin: previous.fapin || result.data.pin || "",
        }));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("BOM AOF1 autofill error:", error);
        }
      }
    };

    loadAutofill();

    return () => {
      controller.abort();
    };
  }, []);

  const firstApplicantIsMinor = (getAge(formData.fadob) ?? 18) < 18;
  const secondApplicantIsMinor = (getAge(formData.sadob) ?? 18) < 18;
  const nomineeIsMinor = (getAge(formData.ndob) ?? 18) < 18;

  const tabs = useMemo(() => [
    { title: "First Personal", heading: "👤 First Applicant Personal Details", fields: firstPersonalFields.filter((field) => (!field.spouse || formData.famstatus === "MARRIED") && (!field.guardian || firstApplicantIsMinor)) },
    { title: "First Address", heading: "🏠 First Applicant Address Details", fields: firstAddressFields },
    { title: "Second Personal", heading: "👤 Second Applicant Personal Details", fields: secondPersonalFields.filter((field) => (!field.spouse || formData.samstatus === "MARRIED") && (!field.guardian || secondApplicantIsMinor)) },
    { title: "Second Address", heading: "🏠 Second Applicant Address Details", fields: secondAddressFields },
    { title: "Nominee", heading: "👥 Nomination Details", fields: nominationFields.filter((field) => field.name === "isnomination" || (formData.isnomination !== "NO" && (!field.nomineeGuardian || nomineeIsMinor))) },
    { title: "Preview", heading: "✅ Preview", fields: [], preview: true },
  ], [firstApplicantIsMinor, formData.famstatus, formData.isnomination, formData.samstatus, nomineeIsMinor, secondApplicantIsMinor]);

  const visibleFields = tabs[currentTab]?.fields || [];
  const firstFieldName = visibleFields[0]?.name || "";
  const lastFieldName = visibleFields.at(-1)?.name || "";

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  useEffect(() => {
    if (!firstFieldName) return undefined;
    const frameId = window.requestAnimationFrame(() => {
      document.getElementById(firstFieldName)?.focus();
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [currentTab, firstFieldName]);

  const handleLastFieldKeyDown = (event, fieldName) => {
    if (fieldName === lastFieldName && event.key === "Tab" && !event.shiftKey) {
      event.preventDefault();
      nextButtonRef.current?.focus();
    }
  };

  const updateValue = (name, rawValue) => {
    const field = allFields.find((item) => item.name === name);
    let value = rawValue;

    if (field?.numeric) {
      value = value.replace(/\D/g, "");
    }

    if (field?.alphaOnly) {
      value = value.replace(/[^A-Za-z ]/g, "");
    }

    if (field?.pan) {
      value = value.toUpperCase();
    }

    setFormData((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: "" }));
    setPdfError("");
  };

  const validateCurrentTab = () => {
    const newErrors = {};
    visibleFields.forEach((field) => {
      const message = validateField(field, formData[field.name], formData);
      if (message) newErrors[field.name] = message;
    });
    setErrors((previous) => ({ ...previous, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const copyFirstAddress = () => {
    setFormData((previous) => ({
      ...previous,
      saadd1: previous.faadd1,
      sacity: previous.facity,
      sadist: previous.fadist,
      sastate: previous.fastate,
      sapin: previous.fapin,
    }));
    setErrors((previous) => ({ ...previous, saadd1: "", sacity: "", sadist: "", sastate: "", sapin: "" }));
  };

  const goNext = () => {
    if (!validateCurrentTab()) return;

    // Same behavior as the SBI Joint form: after the first applicant's
    // address is validated, copy it automatically to the second applicant.
    if (currentTab === 1) {
      copyFirstAddress();
    }

    const nextTab = Math.min(currentTab + 1, tabs.length - 1);
    setCurrentTab(nextTab);
    setMaxAllowedTab((previous) => Math.max(previous, nextTab));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    const allErrors = {};
    allFields.forEach((field) => {
      const message = validateField(field, formData[field.name], formData);
      if (message) allErrors[field.name] = message;
    });
    if (Object.keys(allErrors).length) {
      setErrors(allErrors);
      setPdfError("Please correct the highlighted fields before submitting.");
      return;
    }

    setPdfLoading(true);
    setPdfError("");
    try {
      const postData = new FormData();
      Object.entries(formData).forEach(([name, value]) => {
        postData.append(name, value === SELECT ? "" : String(value ?? ""));
      });
      const response = await fetch(PDF_ENDPOINT, {
        method: "POST",
        body: postData,
        credentials: "include",
        cache: "no-store",
        headers: { Accept: "application/pdf" },
      });
      const contentType = response.headers.get("content-type") || "";
      if (!response.ok || !contentType.toLowerCase().includes("application/pdf")) {
        const responseText = await response.text();
        throw new Error(responseText.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() || `PDF generation failed. HTTP status: ${response.status}`);
      }
      const objectUrl = URL.createObjectURL(await response.blob());
      setPdfUrl((previousUrl) => {
        if (previousUrl?.startsWith("blob:")) URL.revokeObjectURL(previousUrl);
        return objectUrl;
      });
      setShowPdf(true);
    } catch (error) {
      setPdfError(error.message || "Unable to generate PDF preview.");
    } finally {
      setPdfLoading(false);
    }
  };

  const renderField = (field) => {
    const error = errors[field.name];
    return (
      <div key={field.name} className="space-y-2">
        <Label htmlFor={field.name}>
          {field.label}
          {!field.optional && <span className="text-red-500"> *</span>}
        </Label>
        {field.options ? (
          <Select value={formData[field.name]} onValueChange={(value) => updateValue(field.name, value)}>
            <SelectTrigger id={field.name} className={error ? "border-red-500" : ""} onKeyDown={(event) => handleLastFieldKeyDown(event, field.name)}>
              <SelectValue placeholder={`Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <Input
            id={field.name}
            name={field.name}
            type={field.type || "text"}
            value={formData[field.name]}
            placeholder={field.label}
            maxLength={field.maxLength}
            inputMode={field.numeric ? "numeric" : undefined}
            onChange={(event) => updateValue(field.name, event.target.value)}
            onKeyDown={(event) => handleLastFieldKeyDown(event, field.name)}
            className={error ? "border-red-500" : ""}
            autoComplete="on"
          />
        )}
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    );
  };

  if (showPdf && pdfUrl) {
    return <PdfViewerContainer pdfUrl={pdfUrl} title="BOM Joint Account Form" backRoute="/Fill_My_Form" onBack={() => setShowPdf(false)} />;
  }

  const previewGroups = tabs.filter((tab) => !tab.preview).map((tab) => ({
    title: tab.heading.replace(/^[^ ]+ /, ""),
    rows: tab.fields.map((field) => [field.label, field.options?.find(([value]) => value === formData[field.name])?.[1] || formData[field.name]]),
  }));

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">🏦 Bank of Maharashtra Joint Account Opening Form</CardTitle>
            <p className="text-muted-foreground">Fill both applicants' details carefully.</p>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          {tabs.map((tab, index) => (
            <Button key={tab.title} type="button" variant={currentTab === index ? "default" : "outline"} disabled={index > maxAllowedTab || pdfLoading} onClick={() => setCurrentTab(index)}>
              {index + 1}. {tab.title}
            </Button>
          ))}
        </div>

        <div>
          {currentTab < tabs.length - 1 && (
            <Card>
              <CardHeader><CardTitle>{tabs[currentTab].heading}</CardTitle></CardHeader>
              <CardContent className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {visibleFields.map(renderField)}
                </div>
              </CardContent>
            </Card>
          )}

          {currentTab === tabs.length - 1 && (
            <Card>
              <CardHeader><CardTitle>✅ Preview</CardTitle></CardHeader>
              <CardContent className="space-y-8">
                {previewGroups.map((group) => <PreviewSection key={group.title} title={group.title} rows={group.rows} />)}
              </CardContent>
            </Card>
          )}

          {pdfLoading && <div className="mt-6 text-center">Generating PDF, please wait...</div>}
          {pdfError && <div className="mt-6 rounded-lg border border-red-500 p-4 text-red-500">{pdfError}</div>}

          <div className="flex justify-between mt-8">
            <Button type="button" variant="outline" onClick={() => setCurrentTab((tab) => Math.max(0, tab - 1))} disabled={currentTab === 0 || pdfLoading}>← Previous</Button>
            {currentTab === tabs.length - 1 ? (
              <Button type="button" onClick={handleSubmit} disabled={pdfLoading}>{pdfLoading ? "Generating PDF..." : "Submit"}</Button>
            ) : (
              <Button ref={nextButtonRef} type="button" onClick={goNext} disabled={pdfLoading}>{currentTab === tabs.length - 2 ? "Preview" : "Next →"}</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewSection({ title, rows }) {
  return (
    <div className="rounded-2xl border p-6">
      <h3 className="text-xl font-semibold text-center mb-6">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map(([label, value]) => (
          <div key={label} className="grid grid-cols-2 gap-3 border-b pb-2">
            <div className="font-medium text-muted-foreground">{label}</div>
            <div>{value && value !== SELECT ? value : "-"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
